import { useCallback, useState } from 'react';

import { ExtractionContent } from './extraction-content';
import { ReferencesDict } from '../../../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { cn } from '@/helpers/shadCnUtils';
import { TextWithReferences } from '@/new/components/text-references';
import { KimMode } from '@/types';

interface AnswerContentProps {
  content: string;
  reasoning: string;
  references: ReferencesDict;
  flowType: KimMode | undefined;
}

export function AnswerContent({ content, reasoning, references, flowType }: AnswerContentProps) {
  const [showReasoning, setShowReasoning] = useState(false);

  const getContentFromFlowType = useCallback(
    (flowType: KimMode | undefined) => {
      if (flowType === KimMode.ExtractionFlow) {
        try {
          const maybeArray = JSON.parse(content);

          if (Array.isArray(maybeArray)) {
            return <ExtractionContent data={maybeArray} references={references ?? undefined} />;
          }
        } catch {}
      }

      if (flowType == KimMode.DraftFlow) {
        return (
          <div className="my-4 flex w-full flex-col items-center">
            <div className="relative min-h-[800px] w-full max-w-[800px] overflow-x-auto rounded-lg border bg-white px-12 py-10 shadow-lg">
              <div className="markdown-content relative text-justify text-sm text-gray-800">
                <TextWithReferences
                  content={content}
                  referencesDict={references}
                  onReferenceClick={() => {
                    trackCustomEvent(TrackingEventsV2.KimReferenceClicked);
                  }}
                />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="markdown-content text-sm text-gray-800">
          <TextWithReferences
            content={content}
            referencesDict={references}
            onReferenceClick={() => {
              trackCustomEvent(TrackingEventsV2.KimReferenceClicked);
            }}
          />
        </div>
      );
    },
    [content, references],
  );

  return (
    <div className="mx-2 rounded-lg border bg-gray-50 px-5 py-2">
      <>
        {reasoning.trim() !== '' && (
          <div className="mb-2">
            <button
              className="flex items-center gap-1 rounded-lg px-0.5 py-1.5 text-xs font-medium text-gray-700 transition-all duration-200 hover:text-gray-900"
              onClick={() => {
                const newState = !showReasoning;
                setShowReasoning(newState);
                trackCustomEvent(TrackingEventsV2.KimToggleThinking, {
                  action: newState ? 'show' : 'hide',
                });
              }}
            >
              <p>Show thinking</p>

              <span
                className={cn(
                  showReasoning && '-rotate-180',
                  `iconify lucide--chevron-down h-4 w-4 text-gray-500 duration-200`,
                )}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showReasoning ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="my-2 rounded-lg border border-dashed border-gray-200 bg-white p-4 py-3 pb-1">
                <div className="markdown-content text-xs leading-relaxed text-gray-700">
                  <TextWithReferences
                    content={reasoning}
                    referencesDict={references}
                    onReferenceClick={() => {
                      trackCustomEvent(TrackingEventsV2.KimReferenceClicked);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {getContentFromFlowType(flowType)}
      </>
    </div>
  );
}
